import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import useAuthorityLevel from '@/app/hooks/useAuthorityLevel';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import Modal from 'src/app/components/Modal';
import EventForm from 'src/app/components/eventForm';
import { useSession } from 'src/app/hooks/useSession';
import { TeacherDashboardProvider } from './providers/TeacherDashboardProvider';
import { DashboardMetrics } from './dashboard-metrics';
import Survey from 'src/app/components/Survey';
import { useSurvey } from 'src/app/hooks/useSurvey';
import DashboardStudent from './DashboardStudent';
import { ViewEvent } from './ViewEvent';
import Onboarding from 'src/app/components/Onboarding';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import MockQuestionBookCard from './MockQuestionBookCard';
import MockQuestionBook from './MockQuestionBook';
import { DashboardTeacher } from './dashboard-teacher';
import { DashboardContent } from '@/layouts/dashboard';
import { PageCol, PageContent, PageSidebar } from '@/components/custom/page';
import { EventsCalendar } from '@/components/custom/dashboard';

function Dashboard(props) {
  return (
    <TeacherDashboardProvider>
      <ScreenContentDashboard {...props} />
    </TeacherDashboardProvider>
  );
}

function ScreenContentDashboard(props) {
  const { t } = useTranslation();
  const client = useClient();
  const [slots, setSlots] = useState([]);
  const [editingEvent, setEditingEvent] = useState();
  const [events, setEvents] = useState([]);
  const [eventDates, setEventDates] = useState([]);
  const [showSurvey, setShowSurvey] = useState(false);
  const [showFormEvent, setShowFormEvent] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { getStudentSurvey, currentStudentSurvey } = useSurvey();
  const { session } = useSession();
  const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];
  const path = props.path;
  const [showMockQuestionBook, setShowMockQuestionBook] = useState(session?.user?.userDetail?.showQuestionBookBanner);
  const [mockQuestionBook, setmockQuestionBook] = useState({});
  const [mockLoading, setMockLoading] = useState(true);
  const [visibleEvents, setVisibleEvents] = useState(2);

  const eventsToday = (events || []).filter(i => i['initial-date'] === moment(selectedDate).format('YYYY-MM-DD'));
  const authorityLevel = useAuthorityLevel(session);
  const hasMoreEvents = visibleEvents < eventsToday.length;

  const fetchSlots = async () => {
    try {
      const today = moment().format('YYYY-MM-DD');
      const { data } = await client.fetch(`slots?filter[date_between][initial]=${today}&filter[date_between][final]=${today}`);
      setSlots(data);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchEvents = async initDate => {
    try {
      const initialDate = moment(initDate).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(initDate).endOf('month').format('YYYY-MM-DD');

      const { data } = await client.fetch(`events?filter[date_between][initial]=${initialDate}&filter[date_between][final]=${endDate}`);
      setEvents(data);
    } catch (e) {
      console.log(e);
    }
  };

  const confirmDeleteEvent = event => {
    Swal.fire({
      title: t('projects.warningDeleteEvent'),
      text: t('projects.warningDeleteCannotBeUndoneText'),
      confirmButtonText: t('button.yes'),
      showDenyButton: true,
      denyButtonText: t('button.no'),
      showCloseButton: true
    }).then(res => {
      if (res.isConfirmed) {
        deleteEvent(event);
      }
    });
  };

  const deleteEvent = async event => {
    const { error } = await client.delete(['events', event.id]);
    if (!error) {
      toast.success(t('toast.successDeleteEvent'));
      setEvents(prevState => prevState.filter(item => item.id !== event.id));
    }
  };

  const editEvent = event => {
    setEditingEvent(event);
    setShowFormEvent(true);
  };

  const closeFormEvent = () => {
    setShowFormEvent(false);
    setEditingEvent(null);
  };

  const onCloseSurvey = () => {
    setShowSurvey(false);
    getStudentSurvey();
  };

  const onActiveStartDateChange = ({ activeStartDate }) => {
    setSelectedDate(activeStartDate);
    fetchEvents(activeStartDate);
  };

  const getmockQuestionBook = async () => {
    setMockLoading(true);
    try {
      const { data, error } = await client.fetch('question_books/next');
      if (error) {
        toast.error(t('toast.errorGetSimulations'));
      } else {
        setmockQuestionBook(data);
        setMockLoading(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (!profiles.includes('Professor')) {
      fetchSlots();
    }
    fetchEvents(selectedDate);
    getStudentSurvey();
    if (authorityLevel === 'student') {
      getmockQuestionBook();
    }
  }, []);

  useEffect(() => {
    setEventDates((events || []).map(i => moment(i['initial-date']).format('YYYY-MM-DD')));
  }, [events]);

  useEffect(() => {
    const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];
    const newValue = mockQuestionBook && !mockQuestionBook['is-question-book-done'] && session?.user?.userDetail?.showQuestionBookBanner && !mockLoading && !profiles.includes('Administrador');
    setShowMockQuestionBook(newValue);
  }, [mockLoading, mockQuestionBook, session]);

  return (
    <DashboardContent>
      <PageContent>
        <PageCol className="gap-0">{profiles.includes('Administrador') ? <DashboardMetrics /> : profiles.includes('Aluno') ? <DashboardStudent /> : <DashboardTeacher />}</PageCol>

        <PageSidebar
          variant="calendar"
          overflow={false}
        >
          {showMockQuestionBook && profiles.includes('Aluno') && <MockQuestionBookCard mockQuestionBook={mockQuestionBook} />}

          <EventsCalendar
            eventsToday={eventsToday}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            onActiveStartDateChange={onActiveStartDateChange}
            eventDates={eventDates}
            visibleEvents={visibleEvents}
            setVisibleEvents={setVisibleEvents}
            hasMoreEvents={hasMoreEvents}
            editEvent={editEvent}
            confirmDeleteEvent={confirmDeleteEvent}
            setShowFormEvent={setShowFormEvent}
          />
        </PageSidebar>
      </PageContent>

      <Modal
        show={showFormEvent}
        onClose={closeFormEvent}
      >
        <EventForm
          onClose={() => setShowFormEvent(false)}
          editingEvent={editingEvent}
          events={events}
          setEvents={setEvents}
        />
      </Modal>

      <Modal
        show={showSurvey}
        onClose={() => setShowSurvey(false)}
        contentClassName="modal__content--p-md"
      >
        {currentStudentSurvey && (
          <Survey
            studentSurvey={currentStudentSurvey}
            onClose={onCloseSurvey}
          />
        )}
      </Modal>

      {mockQuestionBook && showMockQuestionBook && !mockLoading && profiles.includes('Aluno') && (
        <MockQuestionBook
          setShowMockQuestionBook={setShowMockQuestionBook}
          showMockQuestionBook={showMockQuestionBook}
          mockQuestionBook={mockQuestionBook}
        />
      )}

      <Onboarding
        path={path}
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        profiles={profiles}
        title={t('onboarding.titleDashboard')}
        videoId="oO3DxD_nqws"
      />
    </DashboardContent>
  );
}

export { Dashboard, ViewEvent };
